import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

type AppRole = "admin" | "driver" | "dispatcher" | "service_technician" | "terminal_staff" | "analyst";

interface TenantUser {
  user_id: string;
  email: string;
  full_name: string;
  effective_roles: AppRole[];
  global_roles: AppRole[];
  tenant_only_roles: AppRole[];
}

const ROLE_LABELS: Record<AppRole, string> = {
  admin: "Admin",
  driver: "Driver",
  dispatcher: "Dispatcher",
  service_technician: "Service Technician",
  terminal_staff: "Terminal Staff",
  analyst: "Analyst",
};

const ALL_ROLES: AppRole[] = ["admin", "dispatcher", "driver", "service_technician", "terminal_staff", "analyst"];
const ASSIGNABLE_ROLES: AppRole[] = ALL_ROLES.filter((r) => r !== "driver");

type RoleScope = "all" | "tenant";

interface RoleEntry {
  role: AppRole;
  scope: RoleScope;
}

interface EditUserDialogProps {
  user: TenantUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  tenantName: string;
  isProd: boolean;
}

export function EditUserDialog({ user, open, onOpenChange, tenantId, tenantName, isProd }: EditUserDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [roleEntries, setRoleEntries] = useState<RoleEntry[]>([]);

  useEffect(() => {
    if (user) {
      setName(user.full_name || "");
      setEmail(user.email || "");
      // Build role entries from user data
      const entries: RoleEntry[] = user.effective_roles.map((r) => ({
        role: r,
        scope: user.tenant_only_roles.includes(r) ? "tenant" as RoleScope : "all" as RoleScope,
      }));
      setRoleEntries(entries);
    }
  }, [user]);

  const globalRoleMutation = useMutation({
    mutationFn: async ({ userId, role, add }: { userId: string; role: AppRole; add: boolean }) => {
      if (add) {
        const { error } = await supabase.rpc("upsert_global_role" as any, {
          _target_user_id: userId,
          _role: role,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.rpc("remove_global_role" as any, {
          _target_user_id: userId,
          _role: role,
        });
        if (error) throw error;
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const tenantRoleMutation = useMutation({
    mutationFn: async ({ userId, role, add }: { userId: string; role: AppRole; add: boolean }) => {
      if (add) {
        const { error } = await supabase.rpc("upsert_tenant_user_role" as any, {
          _target_user_id: userId,
          _tenant_id: tenantId,
          _role: role,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.rpc("remove_tenant_user_role" as any, {
          _target_user_id: userId,
          _tenant_id: tenantId,
          _role: role,
        });
        if (error) throw error;
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const isMutating = globalRoleMutation.isPending || tenantRoleMutation.isPending;

  const handleToggleRole = async (role: AppRole, checked: boolean) => {
    if (!user) return;
    if (checked) {
      const scope: RoleScope = "all";
      setRoleEntries((prev) => [...prev, { role, scope }]);
      // "All environments" → insert into global user_roles
      globalRoleMutation.mutate({ userId: user.user_id, role, add: true });
    } else {
      setRoleEntries((prev) => prev.filter((e) => e.role !== role));
      // Remove from both global and tenant tables
      globalRoleMutation.mutate({ userId: user.user_id, role, add: false });
      tenantRoleMutation.mutate({ userId: user.user_id, role, add: false });
    }
  };

  const handleScopeChange = (role: AppRole, scope: RoleScope) => {
    setRoleEntries((prev) =>
      prev.map((e) => (e.role === role ? { ...e, scope } : e))
    );
    if (!user) return;
    if (scope === "all") {
      // Move to global: add global role, remove tenant override
      globalRoleMutation.mutate({ userId: user.user_id, role, add: true });
      tenantRoleMutation.mutate({ userId: user.user_id, role, add: false });
    } else {
      // Move to tenant-only: remove global role, add tenant override
      globalRoleMutation.mutate({ userId: user.user_id, role, add: false });
      tenantRoleMutation.mutate({ userId: user.user_id, role, add: true });
    }
  };

  const handleSave = async () => {
    // Update name if changed
    if (user && name !== (user.full_name || "")) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await supabase.functions.invoke("update-user-name", {
          body: { user_id: user.user_id, full_name: name },
        });
        if (res.error) throw res.error;
      } catch (err: any) {
        toast.error("Failed to update name: " + (err.message || err));
        return;
      }
    }
    queryClient.invalidateQueries({ queryKey: ["users-for-tenant", tenantId] });
    toast.success("User updated");
    onOpenChange(false);
  };

  if (!user) return null;

  const activeRoles = roleEntries.map((e) => e.role);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="User name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-email">Email</Label>
            <Input
              id="edit-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled
              placeholder="Email address"
            />
          </div>

          <div className="space-y-3">
            <Label>Roles</Label>
            <div className="space-y-2">
              {ASSIGNABLE_ROLES.map((role) => {
                 const isActive = activeRoles.includes(role);
                const entry = roleEntries.find((e) => e.role === role);
                return (
                  <div key={role} className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer flex-1">
                      <Checkbox
                        checked={isActive}
                        onCheckedChange={(checked) => handleToggleRole(role, !!checked)}
                        disabled={isMutating}
                      />
                      {ROLE_LABELS[role]}
                    </label>
                    {isActive && (
                      <Select
                        value={entry?.scope ?? "all"}
                        onValueChange={(v) => handleScopeChange(role, v as RoleScope)}
                      >
                        <SelectTrigger className="h-8 w-[160px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All environments</SelectItem>
                          <SelectItem value="tenant">{tenantName} only</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
